// =====================================================
// 📁 ProcessQuotationModal.tsx
// =====================================================

import React, { useEffect, useMemo, useState } from 'react';

import {
  ArrowRightCircle,
  CheckCircle2,
  FileText,
  Receipt,
  XCircle,
} from 'lucide-react';

import FormModal from '../../../Modals/FormModal';

import POSActionButton from '../../pos/credit-note/shared/POSActionButton';

import { useDocumentType } from '../../../../hooks/useDocumentType';
import { DOCUMENT_TYPE_CODES } from '../../../../constants/documentTypeCodes';

interface Props {
  isOpen: boolean;

  quotationId: number;

  loading?: boolean;

  onClose: () => void;

  onProcess: (
    quotationId: number,
    documentTypeId: number
  ) => Promise<void>;
  title?: string;
  description?: string;
  actionLabel?: string;
}

const ProcessQuotationModal: React.FC<Props> = ({
  isOpen,
  quotationId,
  loading = false,
  onClose,
  onProcess,
  title = 'Procesar cotización',
  description = 'Elija el tipo de documento que desea generar para convertir la cotización en venta.',
  actionLabel = 'Procesar',
}) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const {
    documentTypeDropdown,
    fetchDocumentTypeDropdown,
  } = useDocumentType();

  // =====================================================
  // 🔹 Effects
  // =====================================================

  useEffect(() => {
    if (isOpen) {
      fetchDocumentTypeDropdown();
      setSelectedDocumentId(null);
    }
  }, [isOpen]);

  // =====================================================
  // 🔹 Available documents
  // =====================================================

  const availableDocuments = useMemo(() => {
    return (documentTypeDropdown || []).filter(
      (item: any) =>
        item.code === DOCUMENT_TYPE_CODES.INVOICE ||
        item.code === DOCUMENT_TYPE_CODES.TAX_CREDIT
    );
  }, [documentTypeDropdown]);

  // =====================================================
  // 🔹 State
  // =====================================================

  const [selectedDocumentId, setSelectedDocumentId] =
    useState<number | null>(null);

  // =====================================================
  // 🔹 Handlers
  // =====================================================

  const handleProcess = async () => {
    if (!selectedDocumentId) {
      return;
    }

    await onProcess(
      quotationId,
      selectedDocumentId
    );
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="max-w-xl"
    >
      <div className="space-y-6">
        {/* ===================================================== */}
        {/* 🔹 Header */}
        {/* ===================================================== */}

        <div>
          <h3
            className="
              text-base
              font-semibold
              text-black
              dark:text-white
            "
          >
            Seleccione el documento
          </h3>

          <p
            className="
              mt-1
              text-sm
              text-body
            "
          >
            {description}
          </p>
        </div>

        {/* ===================================================== */}
        {/* 🔹 Documents */}
        {/* ===================================================== */}

        <div className="space-y-3">
          {availableDocuments.map((document: any) => {
            const isSelected =
              selectedDocumentId === document.id;

            const isConsumerInvoice =
              document.code === DOCUMENT_TYPE_CODES.INVOICE;

            return (
              <button
                key={document.id}
                type="button"
                onClick={() =>
                  setSelectedDocumentId(document.id)
                }
                className={`
                  w-full

                  rounded-xl
                  border

                  px-4
                  py-4

                  text-left

                  transition-all
                  duration-200

                  ${
                    isSelected
                      ? `
                        border-primary
                        bg-primary/[0.04]
                      `
                      : `
                        border-stroke
                        bg-white

                        hover:border-primary/30
                        hover:bg-gray-50

                        dark:border-strokedark
                        dark:bg-boxdark
                      `
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  {/* Left */}

                  <div className="flex items-center gap-3">
                    <div
                      className={`
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center

                        rounded-lg

                        ${
                          isSelected
                            ? 'bg-primary/10'
                            : 'bg-gray-100 dark:bg-meta-4'
                        }
                      `}
                    >
                      {isConsumerInvoice ? (
                        <Receipt
                          size={18}
                          className="text-primary"
                        />
                      ) : (
                        <FileText
                          size={18}
                          className="text-primary"
                        />
                      )}
                    </div>

                    <div>
                      <h4
                        className="
                          text-sm
                          font-semibold
                          text-black
                          dark:text-white
                        "
                      >
                        {document.name}
                      </h4>

                      <p
                        className="
                          text-xs
                          text-body
                        "
                      >
                        {isConsumerInvoice
                          ? 'Consumidor final'
                          : 'Documento con crédito fiscal'}
                      </p>
                    </div>
                  </div>

                  {/* Right */}

                  {isSelected ? (
                    <CheckCircle2
                      size={20}
                      className="text-primary"
                    />
                  ) : (
                    <div
                      className="
                        h-5
                        w-5

                        rounded-full
                        border
                        border-stroke
                      "
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* ===================================================== */}
        {/* 🔹 Actions */}
        {/* ===================================================== */}

        <div
          className="
            flex
            justify-end
            gap-3

            border-t
            border-stroke

            pt-5

            dark:border-strokedark
          "
        >
          <POSActionButton
            variant="secondary"
            disabled={loading}
            onClick={onClose}
          >
            <div className="flex items-center gap-2">
              <XCircle size={16} />
              Cancelar
            </div>
          </POSActionButton>

          <POSActionButton
            variant="success"
            disabled={
              loading || !selectedDocumentId
            }
            onClick={handleProcess}
          >
            <div className="flex items-center gap-2">
              <ArrowRightCircle size={16} />
              {actionLabel}
            </div>
          </POSActionButton>
        </div>
      </div>
    </FormModal>
  );
};

export default ProcessQuotationModal;
